import { AUTH_PAGE_PATHS } from "@/lib/constants/auth";

/**
 * Auth form field names that must never appear in a URL. A pre-hydration
 * native GET submit (or an old bookmark / history entry from one) would
 * otherwise carry them in the query string.
 *
 * Kept: `token` (invite / reset links), `callbackUrl`, `error`, `message`.
 */
export const SENSITIVE_AUTH_QUERY_PARAMS = [
  "password",
  "newPassword",
  "confirmPassword",
  "email",
  "firstname",
  "lastname",
  "phone",
] as const;

const SENSITIVE_PARAMS_LOWER = new Set(
  SENSITIVE_AUTH_QUERY_PARAMS.map((name) => name.toLowerCase()),
);

function deleteSensitiveParams(params: URLSearchParams): boolean {
  const sensitiveKeys = [...new Set(params.keys())].filter((key) =>
    SENSITIVE_PARAMS_LOWER.has(key.toLowerCase()),
  );
  for (const key of sensitiveKeys) {
    params.delete(key);
  }
  return sensitiveKeys.length > 0;
}

/**
 * `searchParams.get` already decodes once. A crafted link can be encoded
 * again on top of that, so return the value plus up to two more decoded
 * forms, least decoded first.
 */
function decodingLevels(value: string): string[] {
  const levels = [value];
  let current = value;
  for (let i = 0; i < 2; i++) {
    if (!current.includes("%")) break;
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      levels.push(next);
      current = next;
    } catch {
      break;
    }
  }
  return levels;
}

function isSafeRelativePath(value: string): boolean {
  return (
    value.startsWith("/") && !value.startsWith("//") && !value.startsWith("/\\")
  );
}

const CALLBACK_PARAM = "callbackurl";

/** Nested `callbackUrl` values walked before we stop. */
const MAX_CALLBACK_DEPTH = 3;

const PLACEHOLDER_ORIGIN = "http://placeholder.invalid";

/**
 * Drop sensitive keys from a fragment. Handles `#a=b&c=d` and a
 * hash-router fragment `#/route?a=b`. A dangling `?` is removed when
 * the query becomes empty (`#/reset?password=x` → `#/reset`).
 */
function cleanHash(hash: string): { hash: string; changed: boolean } {
  if (hash.length <= 1 || !hash.includes("=")) return { hash, changed: false };
  const body = hash.slice(1);
  const queryStart = body.indexOf("?");
  const prefix = queryStart === -1 ? "" : body.slice(0, queryStart + 1);
  const query = queryStart === -1 ? body : body.slice(queryStart + 1);
  const params = new URLSearchParams(query);
  if (!deleteSensitiveParams(params)) return { hash, changed: false };
  const next = params.toString();
  if (!next) {
    const bare = prefix.endsWith("?") ? prefix.slice(0, -1) : prefix;
    return { hash: bare ? `#${bare}` : "", changed: true };
  }
  return { hash: `#${prefix}${next}`, changed: true };
}

/**
 * A same-origin relative path the URL parser will not rewrite into
 * `//host`. A tab or backslash fails this: browsers drop the tab and
 * turn `\` into `/`, which is how `/%09/evil.example` becomes an open
 * redirect.
 */
function isParsableSameOrigin(candidate: string): boolean {
  if (!isSafeRelativePath(candidate)) return false;
  try {
    const nested = new URL(candidate, PLACEHOLDER_ORIGIN);
    return (
      nested.origin === PLACEHOLDER_ORIGIN &&
      isSafeRelativePath(nested.pathname)
    );
  } catch {
    return false;
  }
}

/**
 * Delete sensitive keys without asking the URL parser to resolve the
 * path. The parser turns `/\t/evil?password=x` into `//evil`, and copying
 * that into the redirect would write the open redirect into `Location`.
 */
function stripKeysFromRaw(candidate: string, depth: number): string | null {
  const hashIndex = candidate.indexOf("#");
  const beforeHash =
    hashIndex === -1 ? candidate : candidate.slice(0, hashIndex);
  const rawHash = hashIndex === -1 ? "" : candidate.slice(hashIndex);
  const queryIndex = beforeHash.indexOf("?");
  const path = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
  const params = new URLSearchParams(
    queryIndex === -1 ? "" : beforeHash.slice(queryIndex + 1),
  );
  const queryChanged = deleteSensitiveParams(params);
  const nestedChanged = rewriteCallbackParams(params, depth + 1);
  const { hash, changed: hashChanged } = cleanHash(rawHash);
  if (!queryChanged && !nestedChanged && !hashChanged) return null;
  const query = params.toString();
  return `${path}${query ? `?${query}` : ""}${hash}`;
}

/**
 * Drop credentials from an absolute callback (`https://evil/?password=x`,
 * `https://user:secret@evil/path`) and keep the absolute URL.
 * `sanitizePostLoginRedirect` still decides whether that URL is a safe
 * post-login target.
 */
function stripAbsolute(candidate: string, depth: number): string | null {
  if (!/^[a-z][a-z0-9+.-]*:/i.test(candidate)) return null;
  let nested: URL;
  try {
    nested = new URL(candidate);
  } catch {
    return null;
  }
  const hadUserinfo = nested.username !== "" || nested.password !== "";
  nested.username = "";
  nested.password = "";
  const queryChanged = deleteSensitiveParams(nested.searchParams);
  const nestedChanged = rewriteCallbackParams(nested.searchParams, depth + 1);
  const { hash, changed: hashChanged } = cleanHash(nested.hash);
  if (!queryChanged && !nestedChanged && !hashChanged && !hadUserinfo) {
    return null;
  }
  nested.hash = hash;
  return nested.toString();
}

function cleanSameOrigin(candidate: string, depth: number): string | null {
  const nested = new URL(candidate, PLACEHOLDER_ORIGIN);
  const queryChanged = deleteSensitiveParams(nested.searchParams);
  const nestedChanged = rewriteCallbackParams(nested.searchParams, depth + 1);
  const { hash, changed: hashChanged } = cleanHash(nested.hash);
  if (!queryChanged && !nestedChanged && !hashChanged) return null;
  return `${nested.pathname}${nested.search}${hash}`;
}

function cleanCallbackCandidate(
  candidate: string,
  depth: number,
): string | null {
  if (isParsableSameOrigin(candidate)) return cleanSameOrigin(candidate, depth);
  // Already protocol-relative. The parser keeps the host, so userinfo
  // can be cleared without turning a tab or backslash into `//host`.
  if (candidate.startsWith("//")) {
    return stripProtocolRelative(candidate, depth);
  }
  // Relative, but a tab or backslash means the parser will not keep our
  // origin. Strip keys from the raw string instead.
  if (candidate.startsWith("/")) return stripKeysFromRaw(candidate, depth);
  return stripAbsolute(candidate, depth);
}

/**
 * `//user:secret@evil.example/path?password=x` → `//evil.example/path`.
 * Rebuild from the parsed host so the result stays protocol-relative.
 */
function stripProtocolRelative(
  candidate: string,
  depth: number,
): string | null {
  let nested: URL;
  try {
    nested = new URL(candidate, PLACEHOLDER_ORIGIN);
  } catch {
    return stripKeysFromRaw(candidate, depth);
  }
  const hadUserinfo = nested.username !== "" || nested.password !== "";
  nested.username = "";
  nested.password = "";
  const queryChanged = deleteSensitiveParams(nested.searchParams);
  const nestedChanged = rewriteCallbackParams(nested.searchParams, depth + 1);
  const { hash, changed: hashChanged } = cleanHash(nested.hash);
  if (!queryChanged && !nestedChanged && !hashChanged && !hadUserinfo) {
    return null;
  }
  nested.hash = hash;
  return `//${nested.host}${nested.pathname}${nested.search}${nested.hash}`;
}

/**
 * Strip credentials from one `callbackUrl` value.
 * Returns null when the value has no sensitive query or fragment keys.
 *
 * The least-decoded form that exposes a sensitive key wins, so encoded
 * characters in the remaining params (`q=a%26b`) are kept as they were
 * instead of being decoded into new params.
 */
function cleanRelativeCallback(value: string, depth: number): string | null {
  if (depth > MAX_CALLBACK_DEPTH) return null;
  for (const candidate of decodingLevels(value)) {
    const cleaned = cleanCallbackCandidate(candidate, depth);
    if (cleaned != null) return cleaned;
  }
  return null;
}

/**
 * Strip credentials nested inside every `callbackUrl` value, in any letter
 * case, including a repeated parameter, a second nested `callbackUrl`, and
 * a `key=value` fragment (`%23`).
 */
function rewriteCallbackParams(
  params: URLSearchParams,
  depth: number,
): boolean {
  if (depth > MAX_CALLBACK_DEPTH) return false;
  const keys = [...new Set(params.keys())].filter(
    (key) => key.toLowerCase() === CALLBACK_PARAM,
  );
  if (keys.length === 0) return false;

  const kept: string[] = [];
  let changed = false;
  for (const key of keys) {
    for (const value of params.getAll(key)) {
      const cleaned = cleanRelativeCallback(value, depth);
      if (cleaned == null) {
        kept.push(value);
      } else {
        kept.push(cleaned);
        changed = true;
      }
    }
  }
  if (!changed) return false;

  for (const key of keys) params.delete(key);
  for (const value of kept) params.append("callbackUrl", value);
  return true;
}

function stripNestedCallback(url: URL): boolean {
  return rewriteCallbackParams(url.searchParams, 0);
}

/**
 * Returns a copy of `url` with sensitive auth params removed (matched
 * case-insensitively, so `?Password=` is caught too), including the same
 * keys nested inside `callbackUrl`, or `null` when there was nothing to strip.
 */
export function stripSensitiveAuthParams(url: URL): URL | null {
  const cleaned = new URL(url.toString());
  const topLevelChanged = deleteSensitiveParams(cleaned.searchParams);
  const nestedChanged = stripNestedCallback(cleaned);
  if (!topLevelChanged && !nestedChanged) return null;
  return cleaned;
}

/**
 * Returns the canonical auth page path when `pathname` is an auth page in
 * any letter case, with or without a trailing slash (`/SIGNIN`, `/signin/`),
 * or a path under one (`/signin/extra`). Otherwise `null`.
 */
export function canonicalAuthPagePath(pathname: string): string | null {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const lower = trimmed.toLowerCase();
  const match = AUTH_PAGE_PATHS.find(
    (route) => lower === route || lower.startsWith(`${route}/`),
  );
  return match ?? null;
}
